import * as Ably from "ably";

let ablyClient = null;
const activeChannels = {};

// Presence requires a clientId on the connection. One stable id per tab keeps
// the two analyst screens (video + keyboard) distinguishable in a room.
const getTabClientId = () => {
  try {
    let id = sessionStorage.getItem("ably_tab_client_id");
    if (!id) {
      id = `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("ably_tab_client_id", id);
    }
    return id;
  } catch (err) {
    return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }
};

export const getAblyClient = () => {
  if (!ablyClient) {
    ablyClient = new Ably.Realtime({
      key: "_5T0zQ.XOp1Fg:li54clPnpFyM2nF1nzt2zJC_ioB2vXBdA_5M2xP_U_0",
      clientId: getTabClientId(),
      autoConnect: true,
      recover: true,
      disconnectedRetryTimeout: 5000,
      suspendedRetryTimeout: 10000,
    });
  }
  return ablyClient;
};

export const subscribeToMatchChannel = async (matchId, callback) => {
  const channelName = `match-${matchId}`;
  const client = getAblyClient();

  if (!activeChannels[channelName]) {
    const channel = client.channels.get(channelName);

    await channel.attach();

    const handler = (msg) => callback(msg);
    channel.subscribe(handler);

    activeChannels[channelName] = { channel, handler };
  }
};

export const unsubscribeFromMatchChannel = (matchId) => {
  const channelName = `match-${matchId}`;
  const entry = activeChannels[channelName];

  if (entry) {
    const { channel, handler } = entry;
    channel.unsubscribe(handler);
    channel.detach();
    delete activeChannels[channelName];
  }
};

const lineupChannelName = (matchId) => `match_lineup_${matchId}`;
const LINEUP_EVENT = "match_lineup_update";

export const subscribeToLineupChannel = async (matchId, callback) => {
  const channelName = lineupChannelName(matchId);
  const client = getAblyClient();

  if (!activeChannels[channelName]) {
    const channel = client.channels.get(channelName);

    await channel.attach();

    const handler = (msg) => callback(msg);
    channel.subscribe(LINEUP_EVENT, handler);

    activeChannels[channelName] = { channel, handler };
  }
};

export const unsubscribeFromLineupChannel = (matchId) => {
  const channelName = lineupChannelName(matchId);
  const entry = activeChannels[channelName];

  if (entry) {
    const { channel, handler } = entry;
    channel.unsubscribe(LINEUP_EVENT, handler);
    channel.detach();
    delete activeChannels[channelName];
  }
};

export const closeAbly = () => {
  if (ablyClient) {
    ablyClient.close();
    ablyClient = null;
    Object.keys(activeChannels).forEach((key) => delete activeChannels[key]);
  }
};

// ─── Video Timestamp Sync via Ably ───────────────────────────────────────────
// Channel name pattern: video_sync_<matchId>_<analystId>
// Mirrors the existing analyst_portal_<analystId> convention.

const videoSyncChannelName = (matchId, analystId) =>
  `video_sync_${matchId}_${analystId}`;

/**
 * Publish the current video timestamp to the sync channel.
 * Called by VideoTabOld (publisher side).
 *
 * @param {string|number} matchId
 * @param {string|number} analystId
 * @param {number} timestamp  - playback position in seconds
 * @param {boolean} playing   - true if video is playing, false if paused
 */
/**
 * @param {string|number} matchId
 * @param {string|number} analystId
 * @param {number} timestamp  - playback position in seconds
 * @param {boolean} playing   - true if video is playing, false if paused
 * @param {number} [_publishedAt] - wallclock ms added by benchmarkService for latency measurement
 */
export const publishVideoTimestamp = (matchId, analystId, timestamp, playing, _publishedAt) => {
  const client = getAblyClient();
  const channel = client.channels.get(videoSyncChannelName(matchId, analystId));
  // Message name is "timestamp" — matches the channel design in the architecture doc (§3.4).
  const payload = { timestamp, playing };
  if (_publishedAt != null) payload._publishedAt = _publishedAt;
  channel.publish("timestamp", payload);
};

/**
 * Subscribe to video timestamp updates.
 * Called by InputBoard (subscriber side).
 *
 * rewind: '1' — Ably will replay the last retained message immediately on subscribe.
 * This lets a late-joining InputBoard get the current timestamp without a REST call
 * (§4.1 — "InputBoard opens late" edge case).
 *
 * Requires channel persistence to be enabled in the Ably dashboard for video_sync_* channels.
 *
 * @param {string|number} matchId
 * @param {string|number} analystId
 * @param {function} callback  - receives the full Ably message; read msg.data.{ timestamp, playing }
 */
export const subscribeToVideoSyncChannel = async (matchId, analystId, callback) => {
  const channelName = videoSyncChannelName(matchId, analystId);
  const client = getAblyClient();

  if (!activeChannels[channelName]) {
    // rewind: '1' replays the last persisted message on connect so late-joiners get an instant seed.
    const channel = client.channels.get(channelName, { params: { rewind: '1' } });
    await channel.attach();

    const handler = (msg) => callback(msg);
    channel.subscribe("timestamp", handler);

    activeChannels[channelName] = { channel, handler };
  }
};

/**
 * Unsubscribe and detach the video sync channel.
 * Call this in the InputBoard cleanup (useEffect return).
 */
export const unsubscribeFromVideoSyncChannel = (matchId, analystId) => {
  const channelName = videoSyncChannelName(matchId, analystId);
  const entry = activeChannels[channelName];

  if (entry) {
    const { channel, handler } = entry;
    channel.unsubscribe("timestamp", handler);
    channel.detach();
    delete activeChannels[channelName];
  }
};
